/**
 * /zh/what-is-product-management — 产品管理中文 SEO 词条/枢纽页。与
 * WhatIsProductManagement.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function WhatIsProductManagementZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/what-is-product-management"
      documentTitle="产品管理是什么？一份通俗指南 · Draftlize"
      documentDescription="产品管理是拥有产品的「做什么」和「为什么」，而不是亲手做。讲清角色、流程、框架，以及它和产品负责人、项目经理有何不同。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 产品管理</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            产品管理是什么？<br/>
            <em>拥有「做什么」与「为什么」。</em>
          </h1>
          <p className="lede">
            <em>产品管理</em>是这样一门学问：决定<em>做什么</em>、<em>为什么</em>做——并确保它真的上线、真的有意义——却不亲手去做那件事本身。产品经理站在用户、生意和工程之间，把一片混乱的需求变成一个拿得出手去辩护的计划。这页把这门学问讲清楚：角色、从想法到上线的流程、PM 倚靠的框架，以及产品管理和产品负责人、项目经理有何不同。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>产品管理到底是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            产品管理是让一个产品交付的价值最大化的实践——靠选对问题、把它框清楚，并在一个「无法靠下命令」的环境里引导工作。PM 拥有的是结果，不是产出：不是「我们上线了功能」，而是「我们上线的东西推动了我们在意的那个数字」。关于这个角色的日常，看 <a href="/zh/blog/what-do-product-managers-do" style={{ color: 'var(--amber)' }}>产品经理到底做什么</a>。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            <em>数字产品管理</em>（digital product management）是同一门学问、专门用在软件产品上——持续交付和数据让「从想法到学习」的回路紧得多。根本不变，变的只是节奏。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">流程</span>
            <h2 className="serif">从想法到上线，<em>分几个阶段。</em></h2>
          </div>
          <p>产品管理不是线性的，但工作会聚成几个可辨认的阶段。每个都连着一份更深的指南。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '策略', hint: '为什么、为谁', body: '定下你服务谁、问题是什么、你的打法为什么赢、你不做什么。下游的一切都服务于此。', href: '/zh/product-strategy' },
            { k: '发现', hint: '什么值得做', body: '跟用户聊、检验假设、在把工程押上去之前消解「做错东西」的风险。', href: '/zh/product-discovery' },
            { k: '优先级', hint: '先做什么', body: '把「好点子多过产能」变成一个你能对怀疑者复述的、拿得出手的顺序。', href: '/zh/prioritization-frameworks' },
            { k: '路线图', hint: '做什么、何时', body: '把计划表达成沿时间的主题或结果，对确定性诚实，松到不必对下季度说谎。', href: '/zh/what-is-a-product-roadmap' },
            { k: '定义', hint: 'spec', body: '写 PRD 和 spec，把一个决策变成团队和工程师不用开会就能做的东西。', href: '/zh/product-requirements-document' },
            { k: '上线与学习', hint: '成没成', body: '上线，对照事先定好的成功指标衡量，再把学到的喂回策略和发现。', href: '/zh/product-launch-checklist' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <a href={f.href} className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3, textDecoration: 'none' }}>{f.k} →</a>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">别混淆角色</span>
            <h2 className="serif">PM vs 产品负责人 <em>vs 项目经理。</em></h2>
          </div>
          <p>三个总被人混为一谈的角色。区别在于各自「拥有」什么。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>角色</th>
              <th>拥有</th>
              <th>时间尺度</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>产品经理（PM）</td><td className="yes">做什么与为什么；结果</td><td>策略 → 上线</td></tr>
            <tr><td>产品负责人（PO）</td><td className="partial">backlog 与 sprint 范围（Scrum）</td><td>一个个 sprint</td></tr>
            <tr><td>项目经理</td><td className="partial">时间线、资源、交付</td><td>一个界定的项目</td></tr>
          </tbody>
        </table>
        <p style={{ maxWidth: 760, marginTop: 18, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>
          一句话：PM 负责「你在不在做对的东西」；产品负责人（一个 Scrum 角色）负责喂给交付的 backlog；项目经理负责让一个界定好的范围按时上线。在小团队里一个人戴三顶帽子——这恰恰是线最容易缠在一起的时候。
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">把这一切串起来的那根线——<em>也会断。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            上面每个阶段都产出决策，且每个阶段压在前一个上面。这份工作真正难的不是任一单独阶段——而是当事情变化时，让从策略经发现、路线图到 spec 的那根线保持完整。这正是 Draftlize 为之而造的那件窄事。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策成为可寻址卡片</h3>
            <p>一个策略赌注、一条发现、一次优先级抉择、一条 spec 需求——每个都是带稳定 ID 的卡片，能在整条链上被引用，而不是埋在六份不同的文档里。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个，依赖它的标 stale</h3>
            <p>产品管理的工作，大半是当现实移动时保持一致。当一个上游决策变了，所有依赖它的下游自动标记 stale——于是一致性由 substrate 追踪，而不是靠 PM 的记忆。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 共享同一份 context</h3>
            <p>PM 越来越多地和 AI agent 一起跑这个回路。Draftlize 是 Claude Code 或 Cursor 经 MCP 读写的那一份共享 context——于是帮你做下一份 spec 的 agent，已经知道其余工作所建立的策略和决策。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          产品管理是拥有「做什么」与「为什么」。这份工作的大半，是在下游一切移动时让它们保持成立。
        </blockquote>
        <cite>用你的方式做工作，别让从策略到 spec 的那根线悄悄绷断。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '产品经理到底做什么？', a: '产品经理决定做什么、为什么，然后让所有人对齐于此——贯穿策略、发现、优先级、路线图、PRD 和上线。他们拥有的是结果，不是代码。日常是客户对话、决策、写作和说不的混合。详见我们《产品经理到底做什么》。' },
            { q: '产品管理和项目管理有什么区别？', a: '产品管理拥有做什么与为什么——选对问题与结果；项目管理拥有交付——让一个界定的范围按时、按预算上线。PM 问「我们在不在做对的东西」，项目经理问「我们有没有把这东西按计划做对」。' },
            { q: '什么是数字产品管理？', a: '它是专门用在软件产品上的产品管理，持续交付和分析让「从想法到学习」的回路快得多。学问是一样的；不同的是节奏和数据所扮演的角色。' },
            { q: '产品经理用哪些框架？', a: '常见的有优先级框架（RICE、MoSCoW、WSJF）、发现方法（机会-方案树、假设映射）和策略框架。功夫在于让框架匹配决策，而不是认定一个、把所有事都硬塞进去。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用一份共享 context 跑产品工作<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            策略、发现、路线图、spec——Draftlize 把贯穿它们的决策作为卡片持有，于是一处改动会标记所有下游，你的 AI agent 每一步读到的都是最新版。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
